import createModule from "../createModule"
import view from "./view"
import { Creators } from "./model"
import { Creators as PortfolioRootCreators } from "modules/portfolio-root/model"
import { getViewType, getPeriod, getPortfolioId, getSelectedModelId, getIsExpanded, getIsActive, getCurrency } from "modules/portfolio-root/selectors/portfolios"
import { getBaseStartDate } from "modules/portfolio-root/selectors/base"
import { getEarliestDate, getLatestDate } from "modules/portfolio-root/selectors/dates"
import { getStartDate, getEndDate, getSelectedDate, getDriftDates, getAiSummaryStartDate } from "selectors/dates"
import { Creators as notificationsCreators } from "modules/notifications/model"
import { getSelectedOption, getFilteredData, getCategoryItemsSubset, getDrift, getPassthroughResourcesStatus } from "./selectors/data"
import { getBaseSignature, getBasePositionSetWithSegment } from "selectors/base"
import { CATEGORIES, TOP_POSITION_COUNT } from "helpers/analyzeConstants"
import { getDisplayType, getDisplayTypeName } from "./selectors/items"
import { getFileName } from "./selectors/file"
import { getFirstBetaDate, getBetaQueryDates } from "./selectors/beta"
import { hasReferenceEquity } from "./selectors/concentration"
import { getDatesAdjusted } from "./selectors/dates"
import { getSegment } from "modules/analyze-header/selectors/segment"
import { getFiltersVisible } from "modules/analyze-header/selectors"
import { getMissingDataFiles, betasAreMissing, betasSubQuery } from "selectors/missingDataFiles"
import { pick } from "lodash"
import { getPortfolioBetaInstrument, getIsInvalidBetaInstrumentReason } from "modules/portfolio-root/selectors/beta-instrument"
import { MODEL_METADATA_QUERY } from "resources/models"

export default createModule({
  view,
  externalProps: (state, props) => ({
    portfolioShortId: state.global.selected.portfolio,
    portfolioId: getPortfolioId(state, props),
    account: state.global.selected.account,
    modelId: getSelectedModelId(state, props),
    driftDates: getDriftDates(state, props),
    startDate: getStartDate(state, props),
    aiSummaryStartDate: getAiSummaryStartDate(state, props),
    endDate: getEndDate(state, props),
    selectedDate: getSelectedDate(state, props),
    viewType: getViewType(state, props),
    base: getBasePositionSetWithSegment(state, props),
    baseSignature: getBaseSignature(state, props),
    baseStartDate: getBaseStartDate(state, props),
    segment: getSegment(state, props),
    missingDataFiles: getMissingDataFiles(state, props),
    displayType: getDisplayType(state, props),
    expand: getIsExpanded(state),
    isActive: getIsActive(state),
    betaInstrument: getPortfolioBetaInstrument(state, props),
    queryDates: getBetaQueryDates(state, props),
    invalidBetaInstrumentReason: getIsInvalidBetaInstrumentReason(state, props),
  }),
  required: ["account", "portfolioShortId", "modelId"],
  data: [{
    resource: "portfolio",
    as: null,
    url: ({ account, portfolioShortId }) => `/api/${account}/p/${portfolioShortId}`,
  }, {
    resource: "beta",
    as: null,
    passthrough: true,
    url: ({ portfolioId, modelId, queryDates, isActive, base, baseSignature, segment, missingDataFiles, expand }) => {
      if (!missingDataFiles || betasAreMissing(missingDataFiles, modelId)) { return null }
      const { from, to } = queryDates

      return {
        id: `${modelId}${portfolioId}${from}${to}${isActive}${baseSignature}${JSON.stringify(segment)}${expand}`,
        query: `query ($modelId: String!, $positionSet: PositionSetInput!, $from: Date!, $to: Date!, $base: [PositionSetInput]) {
          model(id: $modelId) {
            simulation(positionSet: $positionSet, from: $from, to: $to, base: $base, interval: AUTO) {
              ${betasSubQuery(missingDataFiles, ["date", "rolledOverFrom"])}
            }
          }
        }`,
        variables: {
          modelId,
          positionSet: {
            type: "PORTFOLIO",
            id: portfolioId,
            segment,
            expand,
          },
          from,
          to,
          base: isActive ? [base] : undefined,
        },
      }
    },
  }, {
    resource: "dynamicBeta",
    as: null,
    passthrough: true,
    url: ({ portfolioId, modelId, queryDates, isActive, base, baseSignature, segment, expand, betaInstrument, invalidBetaInstrumentReason }) => {
      if (!betaInstrument || invalidBetaInstrumentReason) { return null }

      const { from, to } = queryDates

      return {
        id: `${modelId}${portfolioId}${from}${to}${isActive}${baseSignature}${JSON.stringify(segment)}${expand}${JSON.stringify(betaInstrument)}`,
        query: `query ($modelId: String!, $positionSet: PositionSetInput!, $from: Date!, $to: Date!, $base: [PositionSetInput], $betaInstrument: PositionSetInput!) {
          model(id: $modelId) {
            simulation(positionSet: $positionSet, from: $from, to: $to, base: $base, interval: AUTO) {
              beta(instrument: $betaInstrument) {
                date
                custom: predicted
              }
            }
          }
        }`,
        variables: {
          modelId,
          betaInstrument: pick(betaInstrument, ["id", "type"]),
          positionSet: {
            type: "PORTFOLIO",
            id: portfolioId,
            segment,
            expand,
          },
          from,
          to,
          base: isActive ? [base] : undefined,
        },
      }
    },
  }, {
    resource: "concentration",
    as: null,
    passthrough: true,
    url: ({ portfolioId, modelId, startDate, endDate, segment, expand }) => {
      return {
        id: `${modelId}${portfolioId}${startDate}${endDate}${JSON.stringify(segment)}${expand}`,
        query: `query ($modelId: String!, $positionSet: PositionSetInput!, $from: Date!, $to: Date!) {
          model(id: $modelId) {
            simulation(positionSet: $positionSet, from: $from, to: $to, interval: AUTO) {
              composition(scaleFormat: PERCENT_EQUITY_GMV) {
                date
                gmv
                referenceEquity
                positionsCount
                concentration {
                  max
                  top(count: ${TOP_POSITION_COUNT})
                }
              }
            }
          }
        }`,
        variables: {
          modelId,
          positionSet: {
            type: "PORTFOLIO",
            id: portfolioId,
            segment,
            expand,
          },
          from: startDate,
          to: endDate,
        },
      }
    },
  },
  MODEL_METADATA_QUERY,
  ],
  mapStateToProps: (state, props) => ({
    earliestDate: getEarliestDate(state, props),
    latestDate: getLatestDate(state, props),
    data: getFilteredData(state, props),
    items: getCategoryItemsSubset(state, props),
    options: CATEGORIES,
    period: getPeriod(state, props),
    drift: getDrift(state, props),
    firstDate: getFirstBetaDate(state, props),
    fileName: getFileName(state, props),
    displayTypeName: getDisplayTypeName(state, props),
    selectedOption: getSelectedOption(state, props),
    filtersVisible: getFiltersVisible(state, props),
    modelId: getSelectedModelId(state, props),
    refEquityOverride: hasReferenceEquity(state),
    datesAdjusted: getDatesAdjusted(state),
    currency: getCurrency(state),
    passthroughResourcesStatus: getPassthroughResourcesStatus(state, props),
    ...state.modules.analyzeComposition,
  }),
  actions: {
    ...Creators,
    setDate: PortfolioRootCreators.setDate,
    resetDate: PortfolioRootCreators.resetDate,
    setPeriod: PortfolioRootCreators.setPeriod,
    addNotification: notificationsCreators.add,
  },
})
